/*****************************************************
 * VasteZendingModal.jsx
 * Locatie: src/components/VasteZendingModal.jsx
 * Versie: 1.0.0
 * Datum: 2026-05-19
 ******************************************************
 * Modal voor het aanmaken/bewerken van een vaste
 * (periodieke) zending. Gebruikt in:
 * - Admin aanvragenmodal (tab "Vaste zending")
 * - Klantenportaal CustomerNewRequest
 *
 * Props:
 *   show         {bool}   — modal tonen/verbergen
 *   onClose      {func}   — sluiten
 *   onSave       {func}   — opgeslagen callback (id)
 *   initialData  {object} — voor bewerken (bestaande zending)
 *   klantId      {string} — KlantID
 *   klantNaam    {string} — Klantnaam
 *   prefillData  {object} — route/goederen vanuit aanvraagform
 *****************************************************/

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../LanguageContext';

const G = {
  navy:'#1a364c', gold:'#d4a848', bg:'#f8fafc',
  border:'#e2e8f0', txt:'#1e293b', txt2:'#64748b', txt3:'#94a3b8',
  green:'#16a34a', greenL:'#dcfce7', red:'#dc2626', redL:'#fee2e2',
};

const DAGEN_NL   = ['Maandag','Dinsdag','Woensdag','Donderdag','Vrijdag','Zaterdag','Zondag'];
const DAGEN_KEYS = ['maandag','dinsdag','woensdag','donderdag','vrijdag','zaterdag','zondag'];

function createEmptyForm(prefill = {}) {
  return {
    naam               : prefill.naam || '',
    frequentie         : 'wekelijks',
    wekelijkse_dagen   : ['maandag'],
    maandelijkse_datum : 1,
    vaste_dagen        : [],
    dagen_voor_laad    : 3,
    laad_adres         : prefill.laad_adres || prefill.LaadAdres || '',
    laad_postcode      : prefill.laad_postcode || prefill.LaadPostcode || '',
    laad_plaats        : prefill.laad_plaats || prefill.LaadPlaats || '',
    laad_land          : prefill.laad_land || prefill.LaadLand || 'Nederland',
    los_adres          : prefill.los_adres || prefill.LosAdres || '',
    los_postcode       : prefill.los_postcode || prefill.LosPostcode || '',
    los_plaats         : prefill.los_plaats || prefill.LosPlaats || '',
    los_land           : prefill.los_land || prefill.LosLand || 'Nederland',
    goederen           : prefill.goederen || [],
    services           : prefill.services || {},
    referentie1        : prefill.referentie1 || '',
    opmerkingen        : prefill.opmerkingen || '',
    email_melding      : true,
  };
}

export default function VasteZendingModal({
  show, onClose, onSave,
  initialData = null,
  klantId, klantNaam,
  prefillData = null,
}) {
  const { t } = useLanguage();
  const [form, setForm] = useState(createEmptyForm(prefillData || {}));
  const [opslaan, setOpslaan] = useState(false);
  const [fout, setFout] = useState('');
  const [nieuweDag, setNieuweDag] = useState('');

  useEffect(() => {
    if (initialData) {
      setForm({
        ...initialData,
        wekelijkse_dagen: typeof initialData.wekelijkse_dagen === 'string'
          ? JSON.parse(initialData.wekelijkse_dagen || '["maandag"]')
          : initialData.wekelijkse_dagen || ['maandag'],
        vaste_dagen: typeof initialData.vaste_dagen === 'string'
          ? JSON.parse(initialData.vaste_dagen || '[]')
          : initialData.vaste_dagen || [],
        goederen: typeof initialData.goederen === 'string'
          ? JSON.parse(initialData.goederen || '[]')
          : initialData.goederen || [],
        services: typeof initialData.services === 'string'
          ? JSON.parse(initialData.services || '{}')
          : initialData.services || {},
        email_melding: initialData.email_melding === 1 || initialData.email_melding === true,
      });
    } else if (prefillData) {
      setForm(createEmptyForm(prefillData));
    }
  }, [initialData, prefillData]);

  const upd = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const toggleWekelijksDag = (dag) => {
    setForm(f => {
      const huidig = f.wekelijkse_dagen || [];
      return {
        ...f,
        wekelijkse_dagen: huidig.includes(dag)
          ? huidig.filter(d => d !== dag)
          : [...huidig, dag],
      };
    });
  };

  const voegVasteDagToe = () => {
    if (!nieuweDag) return;
    setForm(f => ({
      ...f,
      vaste_dagen: [...(f.vaste_dagen || []), nieuweDag].sort(),
    }));
    setNieuweDag('');
  };

  const verwijderVasteDag = (dag) => {
    setForm(f => ({ ...f, vaste_dagen: f.vaste_dagen.filter(d => d !== dag) }));
  };

  const handleOpslaan = async () => {
    if (!form.laad_plaats || !form.los_plaats) {
      setFout('Vul laadplaats en losplaats in.'); return;
    }
    if (form.frequentie === 'wekelijks' && (!form.wekelijkse_dagen || !form.wekelijkse_dagen.length)) {
      setFout('Selecteer minimaal één dag.'); return;
    }

    setOpslaan(true); setFout('');
    try {
      const url = initialData
        ? `/api/vaste-zendingen/${initialData.id}`
        : '/api/vaste-zendingen';
      const method = initialData ? 'PUT' : 'POST';

      const payload = {
        ...form,
        klant_id  : klantId,
        klant_naam: klantNaam,
        email_melding: form.email_melding ? 1 : 0,
      };

      const resp = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      });
      const data = await resp.json();
      if (data.success) {
        onSave && onSave(data.id || initialData?.id);
        onClose();
      } else {
        setFout(data.error || 'Opslaan mislukt');
      }
    } catch(e) {
      setFout(e.message);
    }
    setOpslaan(false);
  };

  if (!show) return null;

  const inputStyle = {
    width:'100%', padding:'8px 10px', border:`1px solid ${G.border}`,
    borderRadius:6, fontSize:13, boxSizing:'border-box',
    outline:'none', color:G.txt,
  };
  const labelStyle = { fontSize:12, color:G.txt2, fontWeight:600, marginBottom:4, display:'block' };
  const sectionStyle = { marginBottom:20 };

  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.5)',
      zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center',
    }}>
      <div style={{
        background:'#fff', borderRadius:14, width:'min(95vw,620px)',
        maxHeight:'90vh', overflow:'hidden', display:'flex', flexDirection:'column',
        boxShadow:'0 20px 60px rgba(0,0,0,0.2)',
      }}>
        {/* Header */}
        <div style={{ background:G.navy, padding:'20px 24px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ color:G.gold, fontSize:11, fontWeight:700, letterSpacing:'.1em', marginBottom:4 }}>
              VASTE ZENDING
            </div>
            <h2 style={{ color:'#fff', margin:0, fontSize:18, fontWeight:700 }}>
              {initialData ? 'Vaste zending bewerken' : 'Nieuwe vaste zending'}
            </h2>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.1)', border:'none', color:'#fff', borderRadius:8, padding:'8px 12px', cursor:'pointer', fontSize:16 }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ overflowY:'auto', padding:'24px', flex:1 }}>

          {/* Naam */}
          <div style={sectionStyle}>
            <label style={labelStyle}>Naam (optioneel)</label>
            <input style={inputStyle} value={form.naam}
              onChange={e => upd('naam', e.target.value)}
              placeholder={`${form.laad_plaats || 'Van'} → ${form.los_plaats || 'Naar'}`} />
          </div>

          {/* Route */}
          <div style={{ ...sectionStyle, display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={labelStyle}>Laadplaats *</label>
              <input style={inputStyle} value={form.laad_plaats}
                onChange={e => upd('laad_plaats', e.target.value)} placeholder="Stad" />
            </div>
            <div>
              <label style={labelStyle}>Losplaats *</label>
              <input style={inputStyle} value={form.los_plaats}
                onChange={e => upd('los_plaats', e.target.value)} placeholder="Stad" />
            </div>
            <div>
              <label style={labelStyle}>Laadland</label>
              <input style={inputStyle} value={form.laad_land}
                onChange={e => upd('laad_land', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Losland</label>
              <input style={inputStyle} value={form.los_land}
                onChange={e => upd('los_land', e.target.value)} />
            </div>
          </div>

          {/* Frequentie */}
          <div style={sectionStyle}>
            <label style={labelStyle}>Herhaalfrequentie *</label>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {[
                { val:'dagelijks',   label:'Dagelijks' },
                { val:'wekelijks',   label:'Wekelijks' },
                { val:'maandelijks', label:'Maandelijks' },
                { val:'vaste_dagen', label:'Vaste data' },
              ].map(opt => (
                <button key={opt.val}
                  onClick={() => upd('frequentie', opt.val)}
                  style={{
                    padding:'8px 16px', borderRadius:8, fontSize:13, fontWeight:600,
                    border:`2px solid ${form.frequentie === opt.val ? G.navy : G.border}`,
                    background: form.frequentie === opt.val ? G.navy : '#fff',
                    color: form.frequentie === opt.val ? '#fff' : G.txt2,
                    cursor:'pointer',
                  }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Wekelijkse dagen */}
          {form.frequentie === 'wekelijks' && (
            <div style={sectionStyle}>
              <label style={labelStyle}>Op welke dag(en)?</label>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {DAGEN_NL.map((dag, i) => (
                  <button key={dag}
                    onClick={() => toggleWekelijksDag(DAGEN_KEYS[i])}
                    style={{
                      padding:'6px 12px', borderRadius:8, fontSize:12, fontWeight:600,
                      border:`2px solid ${(form.wekelijkse_dagen || []).includes(DAGEN_KEYS[i]) ? G.navy : G.border}`,
                      background: (form.wekelijkse_dagen || []).includes(DAGEN_KEYS[i]) ? G.navy : '#fff',
                      color: (form.wekelijkse_dagen || []).includes(DAGEN_KEYS[i]) ? '#fff' : G.txt2,
                      cursor:'pointer',
                    }}>
                    {dag.slice(0,2)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Maandelijkse datum */}
          {form.frequentie === 'maandelijks' && (
            <div style={sectionStyle}>
              <label style={labelStyle}>Op de ... e van de maand</label>
              <input type="number" min={1} max={28} style={{ ...inputStyle, width:80 }}
                value={form.maandelijkse_datum}
                onChange={e => upd('maandelijkse_datum', parseInt(e.target.value) || 1)} />
            </div>
          )}

          {/* Vaste dagen */}
          {form.frequentie === 'vaste_dagen' && (
            <div style={sectionStyle}>
              <label style={labelStyle}>Vaste laaddata</label>
              <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                <input type="date" style={{ ...inputStyle, width:'auto' }}
                  value={nieuweDag} onChange={e => setNieuweDag(e.target.value)} />
                <button onClick={voegVasteDagToe}
                  style={{ background:G.navy, color:'#fff', border:'none', borderRadius:6, padding:'8px 14px', cursor:'pointer', fontSize:13 }}>
                  + Toevoegen
                </button>
              </div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {(form.vaste_dagen || []).map(dag => (
                  <span key={dag} style={{ background:G.bg, border:`1px solid ${G.border}`, borderRadius:6, padding:'4px 10px', fontSize:12, display:'flex', alignItems:'center', gap:6 }}>
                    {new Date(dag).toLocaleDateString('nl-NL', { day:'2-digit', month:'short', year:'numeric' })}
                    <button onClick={() => verwijderVasteDag(dag)}
                      style={{ background:'none', border:'none', color:G.red, cursor:'pointer', padding:0, fontSize:14 }}>✕</button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Hoeveel dagen van tevoren */}
          <div style={sectionStyle}>
            <label style={labelStyle}>Aanvraag aanmaken ... dagen voor de laaddatum</label>
            <div style={{ display:'flex', gap:8 }}>
              {[1,2,3,5,7,10,14].map(d => (
                <button key={d} onClick={() => upd('dagen_voor_laad', d)}
                  style={{
                    padding:'6px 12px', borderRadius:8, fontSize:12, fontWeight:600,
                    border:`2px solid ${form.dagen_voor_laad === d ? G.gold : G.border}`,
                    background: form.dagen_voor_laad === d ? G.gold : '#fff',
                    color: form.dagen_voor_laad === d ? '#fff' : G.txt2,
                    cursor:'pointer',
                  }}>
                  {d}d
                </button>
              ))}
            </div>
          </div>

          {/* Email melding */}
          <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', background:G.bg, borderRadius:8, marginBottom:20 }}>
            <input type="checkbox" id="email_melding" checked={form.email_melding}
              onChange={e => upd('email_melding', e.target.checked)}
              style={{ width:16, height:16, cursor:'pointer' }} />
            <label htmlFor="email_melding" style={{ fontSize:13, color:G.txt, cursor:'pointer' }}>
              📧 Email melding sturen als de aanvraag automatisch aangemaakt wordt
            </label>
          </div>

          {/* Foutmelding */}
          {fout && (
            <div style={{ background:G.redL, border:`1px solid ${G.red}`, borderRadius:8, padding:'10px 14px', fontSize:13, color:G.red, marginBottom:16 }}>
              {fout}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'16px 24px', borderTop:`1px solid ${G.border}`, display:'flex', justifyContent:'flex-end', gap:10 }}>
          <button onClick={onClose}
            style={{ padding:'10px 20px', border:`1px solid ${G.border}`, borderRadius:8, background:'#fff', color:G.txt2, fontSize:14, cursor:'pointer' }}>
            Annuleren
          </button>
          <button onClick={handleOpslaan} disabled={opslaan}
            style={{ padding:'10px 24px', border:'none', borderRadius:8, background:G.navy, color:'#fff', fontSize:14, fontWeight:600, cursor:'pointer', opacity: opslaan ? 0.6 : 1 }}>
            {opslaan ? 'Opslaan...' : (initialData ? 'Wijzigingen opslaan' : 'Vaste zending aanmaken')}
          </button>
        </div>
      </div>
    </div>
  );
}
